import { useEffect, useMemo, useState } from 'react';
import { DChip } from '@/components/kit';
import { attachmentBlobUrl, type PriorStudy as Prior } from '../api';
import { dayMonth } from '../format';
import { TechModal } from './TechModal';

const KEY_IMAGES = 3;

function useBlobUrls(ids: string[]) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const key = ids.join(',');
  useEffect(() => {
    let alive = true;
    const made: string[] = [];
    void Promise.all(
      ids.map(async (id) => {
        try {
          const u = await attachmentBlobUrl(id);
          made.push(u);
          if (alive) setUrls((m) => ({ ...m, [id]: u }));
        } catch {
          /* a picture that will not load stays a labelled frame */
        }
      }),
    );
    return () => {
      alive = false;
      made.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return urls;
}

function mb(bytes: number) {
  return bytes >= 1024 * 1024 ? `${Math.round(bytes / (1024 * 1024))} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * THE PRIOR STUDY — a picture browser, not a viewer. Key images first, then
 * "Load all N pictures — X MB"; Zoom and Full screen only. There is no DICOM
 * viewer anywhere on this surface, and a machine that is down still lets you
 * read what came before.
 */
export function PriorStudy({ prior, kindLabel }: { prior: Prior | null | undefined; kindLabel: string }) {
  const [all, setAll] = useState(false);
  const [zoom, setZoom] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const pictures = useMemo(
    () => (prior?.attachments ?? []).filter((a) => a.kind === 'image' || a.mimeType?.startsWith('image/')),
    [prior],
  );
  const shown = all ? pictures : pictures.slice(0, KEY_IMAGES);
  const urls = useBlobUrls(shown.map((p) => p.id));
  const others = (prior?.attachments ?? []).filter((a) => !pictures.includes(a));
  const totalBytes = pictures.reduce((s, p) => s + (p.sizeBytes ?? 0), 0);
  const label = prior ? [dayMonth(prior.date, true), prior.title].filter(Boolean).join(' · ') : '';
  const current = picked ?? shown[0]?.id ?? null;

  if (!prior) {
    return (
      <>
        <div className="card">
          <p className="t-ovl mut">Prior study</p>
          <p className="t-sec mut mt8">No earlier {kindLabel} on file for this patient.</p>
        </div>
      </>
    );
  }

  const fullScreen = () => {
    const u = current ? urls[current] : null;
    if (!u) return;
    const img = document.getElementById(`tech-prior-${current}`);
    if (img && 'requestFullscreen' in img) void (img as HTMLElement).requestFullscreen();
  };

  return (
    <>
      <div className="card">
        <p className="t-ovl mut">Prior study</p>
        <p className="t-strong mt8">{label}</p>
        <p className="t-sec mut">attached for comparison ✓</p>
      </div>
      {pictures.length ? (
        <div className="tech-film">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {shown.map((p) => (
              <button key={p.id} type="button" className="tech-frame" onClick={() => setPicked(p.id)}
                style={{ flex: all ? '1 1 30%' : 1, outline: current === p.id ? '2px solid var(--brand-300)' : 'none' }}
                aria-label={p.name}>
                {urls[p.id] ? <img id={`tech-prior-${p.id}`} src={urls[p.id]} alt={p.name} /> : 'image'}
              </button>
            ))}
          </div>
          <p className="t-cap">{label} — {all ? 'every picture' : 'the marked key images'}</p>
        </div>
      ) : null}
      {others.length ? (
        <div className="card mt8">
          {others.map((o) => <p key={o.id} className="t-sec">{o.name}</p>)}
        </div>
      ) : null}
      {pictures.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          <DChip kind="outline" onClick={() => current && setZoom(current)}>Zoom</DChip>
          <DChip kind="outline" onClick={fullScreen}>Full screen</DChip>
          {!all && pictures.length > KEY_IMAGES ? (
            <button type="button" className="btn btn-outline" style={{ marginLeft: 'auto', height: 40 }} onClick={() => setAll(true)}>
              Load all {pictures.length} pictures{totalBytes ? ` — ${mb(totalBytes)}` : ''}
            </button>
          ) : null}
        </div>
      ) : null}
      <p className="t-sec sec mt8">The study opens as pictures you scroll — there is no DICOM viewer.</p>
      <TechModal open={Boolean(zoom)} onClose={() => setZoom(null)} title={label} width={960}>
        {zoom && urls[zoom] ? (
          <img src={urls[zoom]} alt={label} style={{ width: '100%', marginTop: 12, borderRadius: 'var(--r-md)' }} />
        ) : null}
      </TechModal>
    </>
  );
}
